// ============================================================
// LICENSE TYPE REGISTRY  +  HEADLESS RESOLVER
// ------------------------------------------------------------
// Single source of truth for every license category Licensr
// knows about. This module is intentionally UI-free so it can
// be consumed by:
//   1. The standalone Licensr carousel (acquisition / marketing)
//   2. UpWritr, which drives Licensr headlessly inside a logic
//      chain via prop / context / URL param.
//
// Tiers
//   'live'    -> Licensr runs its own intelligence engine
//                (currently only Liquor / MLCC).
//   'verify'  -> We hand the producer to the State of Michigan's
//                own verification search (deep link).
//   'cached'  -> Roadmap: we sync the state dataset into Supabase
//                on a schedule and serve cached results. Until the
//                pipeline ships, behaves like 'verify' but is
//                labelled as cached-search-in-development.
// ============================================================

export const LICENSE_TYPES = [
  {
    slug: 'liquor',
    label: 'Liquor',
    tagline: 'Class C, taverns, bars, restaurants, breweries',
    icon: '🍸',
    accent: '#f59e0b',
    tier: 'live',
    agency: 'Michigan Liquor Control Commission',
    agencyShort: 'MLCC',
    // 'live' types are resolved internally; no external link.
    deepLink: null,
    note: 'Full Licensr intelligence: license status, 5-year violations, entity standing, risk scoring.',
  },
  {
    slug: 'skilled-trades',
    label: 'Skilled Trades',
    tagline: 'Builders, electrical, mechanical, plumbing',
    icon: '🔧',
    accent: '#38bdf8',
    tier: 'verify',
    agency: 'LARA — Bureau of Construction Codes',
    agencyShort: 'LARA / Accela',
    deepLink: 'https://aca-prod.accela.com/MILARA/GeneralProperty/PropertyLookUp.aspx',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'firstName', 'lastName', 'licenseNumber', 'city'],
    note: 'Live LARA licensee lookup: license status, type, expiration, and disciplinary conditions.',
  },
  {
    slug: 'cannabis',
    label: 'Cannabis',
    tagline: 'Growers, processors, retailers, microbusinesses',
    icon: '🌿',
    accent: '#34d399',
    tier: 'verify',
    agency: 'Cannabis Regulatory Agency',
    agencyShort: 'CRA',
    deepLink: 'https://www.michigan.gov/cra/verify-a-license-1',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'licenseNumber', 'city', 'county'],
    note: 'Live CRA license lookup: adult-use and medical licenses, status, and expiration.',
  },
  {
    slug: 'adult-care',
    label: 'Adult Care',
    tagline: 'Adult foster care, homes for the aged',
    icon: '🧑‍🦽',
    accent: '#c084fc',
    tier: 'verify',
    agency: 'LARA — Adult Foster Care / Homes for the Aged',
    agencyShort: 'LARA (AFC)',
    deepLink: 'https://adultfostercare.apps.lara.state.mi.us',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'licenseNumber', 'city', 'county'],
    note: 'Live AFC / Homes for the Aged lookup: license status, capacity, and posted inspection and investigation reports.',
  },
  {
    slug: 'child-care',
    label: 'Child Care',
    tagline: 'Centers, group & family homes',
    icon: '🧸',
    accent: '#f472b6',
    tier: 'verify',
    agency: 'MiLEAP — Child Care Licensing Bureau',
    agencyShort: 'MiLEAP (CCHIRP)',
    deepLink: 'https://cclb.my.site.com/micchirp/s/statewide-facility-search',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'licenseNumber', 'city', 'county'],
    note: 'Live CCHIRP lookup: license status, capacity, and posted inspection reports.',
  },
  {
    slug: 'pesticide',
    label: 'Pesticide & Herbicide',
    tagline: 'Commercial applicators & firms',
    icon: '🌾',
    accent: '#a3e635',
    tier: 'cached',
    agency: 'MDARD — Pesticide & Plant Pest Management',
    agencyShort: 'MDARD',
    deepLink: 'https://www.michigan.gov/mdard/licensing/pesticide',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'licenseNumber', 'city', 'county'],
    note: 'Live MDARD lookup: pesticide application business licenses and certified categories.',
  },
  {
    slug: 'auto-repair',
    label: 'Auto Repair Facility',
    tagline: 'Registered motor-vehicle repair facilities',
    icon: '🔩',
    accent: '#fb923c',
    tier: 'cached',
    agency: 'Michigan Secretary of State — Repair Facilities',
    agencyShort: 'MI SOS',
    deepLink: 'https://www.michigan.gov/sos/industry-services/repair-facilities',
    // Live state-record lookup via /api/license-lookup (licensr-lookup Apify actor).
    liveLookup: true,
    lookupFields: ['name', 'firstName', 'lastName', 'licenseNumber', 'city', 'county'],
    note: 'Live Secretary of State lookup: repair facility registration and mechanic certifications.',
  },
];

// Fast lookup by slug.
export const LICENSE_TYPE_MAP = LICENSE_TYPES.reduce((acc, t) => {
  acc[t.slug] = t;
  return acc;
}, {});

// Accepts a slug OR a few friendly aliases (so UpWritr / URL params
// can pass loosely-formatted values from upstream class codes etc).
const ALIASES = {
  liquor: 'liquor', alcohol: 'liquor', mlcc: 'liquor', bar: 'liquor', tavern: 'liquor',
  'skilled-trades': 'skilled-trades', skilledtrades: 'skilled-trades', trades: 'skilled-trades',
  contractor: 'skilled-trades', builder: 'skilled-trades', electrical: 'skilled-trades',
  plumbing: 'skilled-trades', mechanical: 'skilled-trades',
  cannabis: 'cannabis', marijuana: 'cannabis', marihuana: 'cannabis', cra: 'cannabis', dispensary: 'cannabis',
  'adult-care': 'adult-care', adultcare: 'adult-care', afc: 'adult-care', 'adult-foster-care': 'adult-care',
  'child-care': 'child-care', childcare: 'child-care', daycare: 'child-care', cchirp: 'child-care',
  pesticide: 'pesticide', herbicide: 'pesticide', applicator: 'pesticide', mdard: 'pesticide',
  'auto-repair': 'auto-repair', autorepair: 'auto-repair', 'repair-facility': 'auto-repair',
  mechanic: 'auto-repair', garage: 'auto-repair', sos: 'auto-repair',
};

export function normalizeType(value) {
  if (!value) return null;
  const key = String(value).trim().toLowerCase().replace(/[\s_]+/g, '-');
  return ALIASES[key] || ALIASES[key.replace(/-/g, '')] || (LICENSE_TYPE_MAP[key] ? key : null);
}

// ------------------------------------------------------------
// HEADLESS RESOLVER
// ------------------------------------------------------------
// Given a license type (slug, alias, or config object) returns a
// normalized result describing what should happen next. Both the
// standalone carousel and UpWritr consume this. UpWritr can call
// resolveLicenseType(type) and act on `.action` without rendering
// any Licensr UI at all.
//
// Returns:
//   {
//     ok: boolean,
//     type: <config object> | null,
//     action: 'run_engine' | 'external_verify' | 'cached_lookup',
//     deepLink: string | null,
//     status: 'live' | 'verify' | 'cached',
//     message: string,
//   }
// ------------------------------------------------------------
export function resolveLicenseType(input) {
  const slug = typeof input === 'object' && input?.slug ? input.slug : normalizeType(input);
  const type = slug ? LICENSE_TYPE_MAP[slug] : null;

  if (!type) {
    return {
      ok: false,
      type: null,
      action: null,
      deepLink: null,
      status: null,
      message: `Unknown license type: ${input}`,
    };
  }

  const actionByTier = {
    live: 'run_engine',
    verify: 'external_verify',
    cached: 'cached_lookup',
  };

  const messageByTier = {
    live: `${type.label}: running Licensr intelligence engine.`,
    verify: `${type.label}: verify directly with ${type.agencyShort}.`,
    cached: `${type.label}: cached lookup in development — verify with ${type.agencyShort} for now.`,
  };

  return {
    ok: true,
    type,
    action: actionByTier[type.tier],
    deepLink: type.deepLink,
    status: type.tier,
    message: messageByTier[type.tier],
    // Additive (non-breaking): present when Licensr can pull the state record live.
    // POST { category: slug, ...fields, hubUserId } to liveLookup.endpoint.
    liveLookup: type.liveLookup
      ? { endpoint: '/api/license-lookup', category: type.slug, fields: type.lookupFields || [] }
      : null,
  };
}

// Convenience: read an initial license type from the URL.
// Supports ?type=cannabis and ?embed=1 (UpWritr embed mode).
export function readUrlContext(search = (typeof window !== 'undefined' ? window.location.search : '')) {
  const params = new URLSearchParams(search || '');
  return {
    type: normalizeType(params.get('type')),
    embed: params.get('embed') === '1' || params.get('embed') === 'true',
  };
}
